import { FormikProvider, useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import { Container, Form, TabContent, TabPane } from 'reactstrap'
import * as Yup from 'yup'
import { handleImagesPayload, handleShouldImagesUpdate } from '../../api/images/helpers'
import images from '../../api/images/hooks'
import projects from '../../api/projects/hooks'
import { ENTITY_TYPE } from '../../common/constants'
import AppPermissionsSwitcher from '../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../Components/AppPermissionsSwitcher/constants/actions'
import FormActionButtons from '../../Components/Common/FormActionButtons'
import Loader from '../../Components/Common/Loader'
import PageTitle from '../../Components/Common/PageTitle'
import { DocumentFormTab } from '../../Components/Documents'
import { useMapFormConfig, useRealEstateMaterials3DVideoFormConfig } from '../../Components/Hooks/FormHooks'
import ImagesFormTab from '../../Components/ImagesFormTab/ImagesFormTab'
import MapFormTab, { MapFormTabMain, MapFormTabSide } from '../../Components/MapFormTab/MapFormTab'
import { useFormValidationErrorModal } from '../../Components/Modals/FormValidationErrorModal/useFormValidationErrorModal'
import ProjectFormDescriptionTab, {
    ProjectFormDescriptionTabMain,
    ProjectFormDescriptionTabSide,
} from '../../Components/ProjectFormDescriptionTab/ProjectFormDescriptionTab'
import ProjectFormGeneralTab, {
    ProjectFormGeneralTabMain,
    ProjectFormGeneralTabSide,
} from '../../Components/ProjectFormGeneralTab/ProjectFormGeneralTab'
import PropertyFormMaterialsTab from '../../Components/PropertyFormMaterialsTab/PropertyFormMaterialsTab'
import VirtualWalkVideoTab from '../../Components/VirtualWalkVideoTab/VirtualWalkVideoTab'
import { useInitializeLocationFilters } from '../../hooks/useInitializeLocationFilters'
import { resetFormFilters } from '../../store/formFilters'
import { changeTranslationObject } from '../../utils/general'
import { projectGeneralFormTabs } from './constants'
import { useProjectDescriptionFormConfig, useProjectGeneralFormConfig } from './hooks'

const ProjectEdit = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const { id } = useParams()
    const { t } = useTranslation()
    const module = 'project'

    const currentLang = localStorage.getItem('i18nextLng')

    // tabs definition
    const tabs = projectGeneralFormTabs(true)
    const materialsChildTabs = tabs.find((x) => x.name === 'materials')?.children ?? []

    const [activeTab, setActiveTab] = useState(location?.state?.activeTab ?? tabs[0]?.name)
    const [activeMaterialsTab, setActiveMaterialsTab] = useState(null)

    const { isLoading: fetchingProject, data: projectData } = projects.GetProject.useQuery(id)
    const { mutate: updateProject } = projects.CreateUpdateProject.useMutation()

    const { isLoading: fetchingPhotos, data: photosData } = images.GetImages.useQuery({ module, parent: id, collection: 'photo' })
    const { isLoading: fetchingGroundPlans, data: groundPlansData } = images.GetImages.useQuery({ module, parent: id, collection: 'ground_plan' })
    const { mutate: updateImages } = images.UpdateImages.useMutation()

    const project = projectData?.item ?? null
    const photos = photosData ?? []
    const groundPlans = groundPlansData ?? []

    // used for images
    const entityData = useMemo(
        () => ({
            parent: id,
            parentInternalAppId: project?.internalAppId,
            city: project?.city,
            widerArea: project?.widerArea,
        }),
        [id, project?.internalAppId, project?.city, project?.widerArea]
    )

    useInitializeLocationFilters(project)
    const { generalInputs, generalValidationConfig } = useProjectGeneralFormConfig(project)
    const { descriptionInputs, descriptionValidationConfig } = useProjectDescriptionFormConfig(project?.translations)
    const { mapInputs, mapValidationConfig } = useMapFormConfig(project)
    const { materials3DVideoInputs, materials3DVideoValidationConfig } = useRealEstateMaterials3DVideoFormConfig(project)

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            general: generalValidationConfig.initialValues ?? {},
            description: descriptionValidationConfig.initialValues ?? {},
            map: mapValidationConfig.initialValues ?? {},
            virtualWalkVideo: materials3DVideoValidationConfig.initialValues ?? {},
        },
        validationSchema: Yup.object({
            general: generalValidationConfig.validationSchema ?? Yup.object({}),
            description: descriptionValidationConfig.validationSchema ?? Yup.object({}),
            map: mapValidationConfig.validationSchema ?? Yup.object({}),
            virtualWalkVideo: materials3DVideoValidationConfig.validationSchema ?? Yup.object({}),
        }),
        onSubmit: (values, { setSubmitting }) => {
            const generalValues = generalValidationConfig.prepareModelForSubmit(values.general)
            const translations = changeTranslationObject(values.description, 'project_title', 'project_description', 'project_offer_description')

            const submitValues = {
                ...generalValues,
                ...values.map,
                ...values.virtualWalkVideo,
                translations,
            }

            const images = handleImagesPayload(module, id)
            const shouldUpdateImages = handleShouldImagesUpdate(images)
            const shouldUpdateProject = formik.dirty || !shouldUpdateImages

            if (shouldUpdateProject) {
                updateProject({ data: submitValues, isEdit: true })
            }

            if (shouldUpdateImages) {
                updateImages(images)
            }

            setSubmitting(false)
        },
    })

    useEffect(() => {
        return () => {
            dispatch(resetFormFilters())
        }
    }, [dispatch])

    useFormValidationErrorModal(formik, [generalInputs, descriptionInputs, mapInputs, materials3DVideoInputs])

    const handleTabChange = (tab) => {
        if (activeTab === tab) return

        if (tab === 'materials') {
            const photosTab = materialsChildTabs[0].name
            setActiveMaterialsTab(photosTab)
        }

        setActiveTab(tab)
    }

    const handleMaterialsTabChange = (tab) => {
        if (activeMaterialsTab !== tab) {
            setActiveMaterialsTab(tab)
        }
    }

    const renderFormButtons = () => <FormActionButtons disabled={false} hasCancel={true} cancelAction={() => history.push(`/projects/${id}`)} />

    if (fetchingProject) {
        return (
            <div className="page-content">
                <Container fluid>
                    <Loader />
                </Container>
            </div>
        )
    }

    return (
        <div className="page-content">
            <AppPermissionsSwitcher permission="ModulePermissions" module={location} action={action.edit}>
                {({ hasModulePermission }) => hasModulePermission === false && <Redirect to="/projects" />}
            </AppPermissionsSwitcher>
            <Container fluid>
                <FormikProvider value={formik}>
                    <Form onSubmit={(e) => formik.handleSubmit(e)}>
                        <PageTitle title={`${t('page.edit')} ${project?.translations[currentLang]?.projectTitle}`}>{renderFormButtons()}</PageTitle>

                        <TabContent activeTab={activeTab} className="text-muted">
                            {activeTab === 'general' && (
                                <TabPane tabId="general">
                                    <ProjectFormGeneralTab
                                        subform="general"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={generalInputs}
                                        formik={formik}
                                        module={location}
                                        owner={project?.owner}
                                        handleTabChange={handleTabChange}
                                    >
                                        <ProjectFormGeneralTabSide />
                                        <ProjectFormGeneralTabMain />
                                    </ProjectFormGeneralTab>
                                </TabPane>
                            )}
                            {activeTab === 'materials' && (
                                <TabPane tabId="materials">
                                    <PropertyFormMaterialsTab>
                                        <TabContent activeTab={activeMaterialsTab} className="text-muted">
                                            {activeMaterialsTab === 'photos' && (
                                                <TabPane tabId="photos">
                                                    <ImagesFormTab
                                                        module={module}
                                                        collection={'photo'}
                                                        url={`${module}/photo`}
                                                        imageData={photos}
                                                        entityData={entityData}
                                                        isFetching={fetchingPhotos}
                                                        isSubmitting={formik.isSubmitting}
                                                        activeTab={activeTab}
                                                        activeChildTab={activeMaterialsTab}
                                                        tabs={tabs}
                                                        childTabs={materialsChildTabs}
                                                        handleTabChange={handleTabChange}
                                                        handleChildTabChange={handleMaterialsTabChange}
                                                    />
                                                </TabPane>
                                            )}
                                            {activeMaterialsTab === 'groundPlans' && (
                                                <TabPane tabId="groundPlans">
                                                    <ImagesFormTab
                                                        module={module}
                                                        collection={'ground_plan'}
                                                        url={`${module}/ground_plan`}
                                                        imageData={groundPlans}
                                                        entityData={entityData}
                                                        isFetching={fetchingGroundPlans}
                                                        isSubmitting={formik.isSubmitting}
                                                        activeTab={activeTab}
                                                        activeChildTab={activeMaterialsTab}
                                                        tabs={tabs}
                                                        childTabs={materialsChildTabs}
                                                        handleTabChange={handleTabChange}
                                                        handleChildTabChange={handleMaterialsTabChange}
                                                    />
                                                </TabPane>
                                            )}
                                            {activeMaterialsTab === '3dVideo' && (
                                                <TabPane tabId="3dVideo">
                                                    <VirtualWalkVideoTab
                                                        subform="virtualWalkVideo"
                                                        tabs={tabs}
                                                        activeTab={activeTab}
                                                        activeMaterialsTab={activeMaterialsTab}
                                                        materialsTabs={materialsChildTabs}
                                                        inputs={materials3DVideoInputs}
                                                        formik={formik}
                                                        handleTabChange={handleTabChange}
                                                        handleMaterialsTabChange={handleMaterialsTabChange}
                                                    />
                                                </TabPane>
                                            )}
                                        </TabContent>
                                    </PropertyFormMaterialsTab>
                                </TabPane>
                            )}
                            {activeTab === 'map' && (
                                <TabPane tabId="map">
                                    <MapFormTab
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={mapInputs}
                                        formik={formik}
                                        entityType={ENTITY_TYPE.PROJECT}
                                        handleTabChange={handleTabChange}
                                    >
                                        <MapFormTabSide />
                                        <MapFormTabMain />
                                    </MapFormTab>
                                </TabPane>
                            )}
                            {activeTab === 'description' && (
                                <TabPane tabId="description">
                                    <ProjectFormDescriptionTab
                                        subform="description"
                                        tabs={tabs}
                                        activeTab={activeTab}
                                        inputs={descriptionInputs}
                                        formik={formik}
                                        handleTabChange={handleTabChange}
                                    >
                                        <ProjectFormDescriptionTabSide />
                                        <ProjectFormDescriptionTabMain />
                                    </ProjectFormDescriptionTab>
                                </TabPane>
                            )}
                        </TabContent>
                    </Form>
                </FormikProvider>

                {/* documents are saved via dropzone, not via the page's Formik instance */}
                {activeTab === 'documents' && (
                    <TabPane tabId="documents">
                        <DocumentFormTab tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} documentModule={module} />
                    </TabPane>
                )}
            </Container>
        </div>
    )
}

export default ProjectEdit
